import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExplanationBox } from '../components/ExplanationBox';
import { api, type StartAttemptResponse } from '../lib/api';
import { ErrorView } from '../components/ErrorView';
import { Loader } from '../components/Loader';
import { Progress } from '../components/Progress';
import { Timer } from '../components/Timer';
import { haptic } from '../lib/telegram';
import { BookmarkButton } from '../components/BookmarkButton';
import { ZoomableImage } from '../components/ZoomableImage';

// Стан однієї відповіді — для блокування подвійних кліків і відображення фідбеку
type AnswerState = {
  selectedId: number | null;
  isCorrect: boolean;
  correctAnswerId: number | null;
  explanation: string | null;
};

export function Test() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Дані спроби можуть прийти через router state (зі старту) або їх треба завантажувати окремо.
  // Ми завжди стартуємо тест із Categories, тож state буде. Якщо немає — повертаємо назад.
  const initial = (location.state as StartAttemptResponse | null) ?? null;
  const [data] = useState<StartAttemptResponse | null>(initial);

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, AnswerState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const questionStartRef = useRef<number>(Date.now());

  useEffect(() => {
    questionStartRef.current = Date.now();
  }, [index]);

  const total = data?.questions.length ?? 0;
  const current = data?.questions[index];
  const currentAnswer = current ? answers[current.id] : undefined;

  // Чи дані помітно валідні
  if (!attemptId) return <ErrorView error={new Error('Невалідний ідентифікатор спроби')} />;
  if (!data) {
    // Користувач відкрив URL напряму — повертаємо до старту
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-tg-hint">Тест не запущено. Поверніться до вибору категорії.</p>
        <button onClick={() => navigate('/categories', { replace: true })} className="btn-primary">
          До категорій
        </button>
      </div>
    );
  }

  async function handlePick(answerId: number) {
    if (submitting || !current) return;
    if (currentAnswer) return; // вже відповіли
    setSubmitting(true);
    setError(null);
    const timeMs = Date.now() - questionStartRef.current;
    try {
      const res = await api.submitAnswer(attemptId!, {
        questionId: current.id,
        answerId,
        timeMs,
      });
      setAnswers((prev) => ({
        ...prev,
        [current.id]: {
          selectedId: answerId,
          isCorrect: res.isCorrect,
          correctAnswerId: res.correctAnswerId,
          explanation: res.explanation,
        },
      }));
      if (res.isCorrect) haptic.success();
      else haptic.error();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка відправки');
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  }

  async function next() {
    haptic.light();
    if (index < total - 1) {
      setIndex(index + 1);
    } else {
      await finish();
    }
  }

  async function finish() {
    setSubmitting(true);
    try {
      await api.finishAttempt(attemptId!);
      haptic.success();
      navigate(`/result/${attemptId}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка завершення');
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-tg-secondary-bg bg-tg-bg/95 backdrop-blur">
        <div className="space-y-2 px-4 pt-[env(safe-area-inset-top)] pb-3">
          <div className="flex items-center justify-between pt-3">
            <button
              onClick={() => {
                if (confirm('Перервати тест? Прогрес не буде збережено.')) navigate('/categories', { replace: true });
              }}
              className="text-sm text-tg-hint"
            >
              ← Вийти
            </button>
            {/* Таймер тільки для офіційного іспиту/марафону. Для тренувань — без обмежень. */}
            {data.timeLimitSeconds > 0 ? (
              <Timer totalSeconds={data.timeLimitSeconds} onExpire={finish} />
            ) : (
              <span className="text-xs text-tg-hint">без часу</span>
            )}
          </div>
          <Progress current={index + 1} total={total} />
        </div>
      </header>

      <div className="space-y-2 p-3">
        {error && (
          <div className="card border border-tg-destructive text-sm text-tg-destructive">{error}</div>
        )}

        {current && (
          <>
            <article className="rounded-xl bg-tg-secondary-bg p-3.5">
              <div className="mb-2.5 flex items-start justify-between gap-2">
                <p className="text-base leading-snug">{current.textUk}</p>
                <BookmarkButton questionId={current.id} />
              </div>
              {current.imageUrl && <ZoomableImage src={current.imageUrl} />}
            </article>

            <div className="space-y-2">
              {current.answers.map((a, idx) => {
                const isPicked = currentAnswer?.selectedId === a.id;
                const isRightOne = currentAnswer?.correctAnswerId === a.id;
                let cls = 'flex w-full items-center gap-3 rounded-xl bg-tg-secondary-bg p-3 text-left text-[15px] leading-snug transition';
                if (currentAnswer) {
                  if (isRightOne) cls += ' border border-tg-success bg-tg-success/10';
                  else if (isPicked) cls += ' border border-tg-destructive bg-tg-destructive/10';
                  else cls += ' opacity-60';
                } else {
                  cls += ' hover:bg-tg-bg/40';
                }
                return (
                  <button
                    key={a.id}
                    disabled={submitting || !!currentAnswer}
                    onClick={() => handlePick(a.id)}
                    className={cls}
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tg-bg text-xs font-semibold">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="flex-1">{a.textUk}</span>
                    {currentAnswer && isRightOne && <span className="shrink-0">✓</span>}
                    {currentAnswer && isPicked && !isRightOne && <span className="shrink-0">✗</span>}
                  </button>
                );
              })}
            </div>

            {/* Пояснення — за кнопкою (не вивалюється саме), з оцінкою 👍/👎 */}
            {currentAnswer?.explanation && (
              <ExplanationBox
                key={current.id}
                questionId={current.id}
                explanation={currentAnswer.explanation}
              />
            )}

            {currentAnswer && (
              <button onClick={next} disabled={submitting} className="btn-primary w-full">
                {index < total - 1 ? 'Наступне питання' : 'Завершити'}
              </button>
            )}
            {!currentAnswer && submitting && <Loader />}
          </>
        )}
      </div>
    </>
  );
}
